// src/components/PositionSelect.jsx
//
// The position picker: a searchable, department-grouped dropdown.
//
// It was a native <select> holding thirty-one options in one flat run,
// ordered by seniority. That ordering is correct for a chart and useless for
// finding a title — reaching "Senior QA Engineer" meant reading past Solution
// Architect, Business Analyst, UI/UX Designer and Senior Developer, because
// the one thing the list was NOT sorted by is the thing you are looking for.
//
// Sections and a search box answer that: type three letters, or jump to the
// department the person works in. Both routes, because either can be the fast
// one depending on whether you know the exact title.
//
// Grouping alone would have fitted in <optgroup> and kept the native control,
// which is genuinely the safest thing to open inside a scrolling dialog. It
// was not enough on its own — a native select cannot be typed into, and with
// six sections you still scroll. So this is a custom panel, rendered through
// the hardened PortalDropdown: anchored to its trigger, re-placed on scroll,
// clamped into the viewport.
import React, { useEffect, useMemo, useRef, useState } from "react";
import PortalDropdown from "./PortalDropdown";
import "../assets/css/positionSelect.css";

const PositionSelect = ({
  value,
  options = [],
  onChange,
  disabled = false,
  placeholder = "Set position",
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const buttonRef = useRef(null);
  const searchRef = useRef(null);

  const selected = options.find(
    (o) => String(o.DesignationCode) === String(value),
  );

  /* Grouped in the order the server sent them. DepartmentRank is already
     applied by sp_Designation_GetAll, so preserving arrival order preserves
     the intended section order without re-deciding it here — two places
     sorting the same list is two places to disagree. */
  const groups = useMemo(() => {
    const term = query.trim().toLowerCase();
    const out = [];
    const index = new Map();

    options
      .filter(
        (o) =>
          !term ||
          String(o.DesignationName || "").toLowerCase().includes(term) ||
          String(o.Department || "").toLowerCase().includes(term),
      )
      .forEach((o) => {
        const dept = o.Department || "Other";
        if (!index.has(dept)) {
          index.set(dept, { dept, items: [] });
          out.push(index.get(dept));
        }
        index.get(dept).items.push(o);
      });

    return out;
  }, [options, query]);

  const matchCount = groups.reduce((n, g) => n + g.items.length, 0);

  /* Cleared on every open, not on close: leaving the last search in place
     means the next person to open it sees a filtered list with no obvious
     reason, which reads as options having gone missing. */
  useEffect(() => {
    if (!open) return;
    setQuery("");
    const id = setTimeout(() => searchRef.current?.focus(), 0);
    return () => clearTimeout(id);
  }, [open]);

  const pick = (code) => {
    setOpen(false);
    onChange?.(code);
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={`ps-trigger ${selected ? "is-set" : ""}`}
        disabled={disabled}
        title={
          selected
            ? `${selected.DesignationName} · ${selected.Department || "Other"}`
            : placeholder
        }
        onClick={() => setOpen((v) => !v)}
      >
        <span className="ps-trigger__text">
          {selected ? selected.DesignationName : placeholder}
        </span>
        <i className={`bi bi-chevron-${open ? "up" : "down"}`} />
      </button>

      <PortalDropdown
        isOpen={open}
        onClose={() => setOpen(false)}
        anchorRef={buttonRef}
        minWidth={248}
      >
        <div className="ps-panel" onKeyDown={(e) => e.key === "Escape" && setOpen(false)}>
          <div className="ps-search">
            <i className="bi bi-search" />
            <input
              ref={searchRef}
              value={query}
              placeholder="Search a position…"
              onChange={(e) => setQuery(e.target.value)}
            />
            {query && (
              <button
                type="button"
                className="ps-search__clear"
                onClick={() => setQuery("")}
                aria-label="Clear"
              >
                <i className="bi bi-x-lg" />
              </button>
            )}
          </div>

          <div className="ps-list">
            {/* Always first and never filtered out: clearing a position is
                an action, not one of the options, and hunting for it behind
                a search term nobody typed for it would be absurd. */}
            <button
              type="button"
              className={`ps-option ps-option--none ${!selected ? "is-current" : ""}`}
              onClick={() => pick("")}
            >
              No position
              {!selected && <i className="bi bi-check2" />}
            </button>

            {groups.map((g) => (
              <div className="ps-group" key={g.dept}>
                <div className="ps-group__head">{g.dept}</div>
                {g.items.map((o) => {
                  const isCurrent =
                    String(o.DesignationCode) === String(value);
                  return (
                    <button
                      type="button"
                      key={o.DesignationCode}
                      className={`ps-option ${isCurrent ? "is-current" : ""}`}
                      onClick={() => pick(o.DesignationCode)}
                    >
                      {o.DesignationName}
                      {isCurrent && <i className="bi bi-check2" />}
                    </button>
                  );
                })}
              </div>
            ))}

            {matchCount === 0 && (
              <p className="ps-empty">Nothing matches “{query}”.</p>
            )}
          </div>
        </div>
      </PortalDropdown>
    </>
  );
};

export default PositionSelect;
